import api from './api'
import type {
  BroadcastCampaign,
  BroadcastCampaignLog,
  BroadcastCampaignStartResult,
  BroadcastCampaignStatus,
  BroadcastImportSummary,
  BroadcastList,
  BroadcastListContact,
  BroadcastValidationSummary,
  PaginatedResponse,
} from '@/types'
import type {
  ApiResponse,
  CreateBroadcastCampaignRequest,
  CreateBroadcastListContactRequest,
  CreateBroadcastListRequest,
} from '@/types/api'

const BroadcastsService = {
  async listLists(params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<BroadcastList>> {
    const response = await api.get<PaginatedResponse<BroadcastList>>('/api/v1/broadcast/lists', {
      params,
    })
    return response.data
  },

  async createList(data: CreateBroadcastListRequest): Promise<BroadcastList> {
    const response = await api.post<ApiResponse<BroadcastList>>('/api/v1/broadcast/lists', data)
    return response.data.data
  },

  async deleteList(listId: string): Promise<void> {
    await api.delete(`/api/v1/broadcast/lists/${listId}`)
  },

  async listContacts(
    listId: string,
    params?: { page?: number; per_page?: number; search?: string },
  ): Promise<PaginatedResponse<BroadcastListContact>> {
    const response = await api.get<PaginatedResponse<BroadcastListContact>>(
      `/api/v1/broadcast/lists/${listId}/contacts`,
      { params },
    )
    return response.data
  },

  async createContact(
    listId: string,
    data: CreateBroadcastListContactRequest,
  ): Promise<BroadcastListContact> {
    const response = await api.post<ApiResponse<BroadcastListContact>>(
      `/api/v1/broadcast/lists/${listId}/contacts`,
      data,
    )
    return response.data.data
  },

  async importFromGlobal(
    listId: string,
    contactIds: string[],
  ): Promise<{ imported: number; skipped: number; invalid: number }> {
    const response = await api.post<
      ApiResponse<{ imported: number; skipped: number; invalid: number }>
    >(`/api/v1/broadcast/lists/${listId}/contacts/import-from-global`, {
      contact_ids: contactIds,
    })
    return response.data.data
  },

  async importCsv(listId: string, file: File): Promise<BroadcastImportSummary> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post<ApiResponse<BroadcastImportSummary>>(
      `/api/v1/broadcast/lists/${listId}/contacts/import-csv`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    return response.data.data
  },

  async validateList(listId: string): Promise<BroadcastValidationSummary> {
    const response = await api.post<ApiResponse<BroadcastValidationSummary>>(
      `/api/v1/broadcast/lists/${listId}/validate`,
    )
    return response.data.data
  },

  async listCampaigns(params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<BroadcastCampaign>> {
    const response = await api.get<PaginatedResponse<BroadcastCampaign>>('/api/v1/broadcast/campaigns', {
      params,
    })
    return response.data
  },

  async createCampaign(data: CreateBroadcastCampaignRequest): Promise<BroadcastCampaign> {
    const response = await api.post<ApiResponse<BroadcastCampaign>>('/api/v1/broadcast/campaigns', data)
    return response.data.data
  },

  async startCampaign(campaignId: string): Promise<BroadcastCampaignStartResult> {
    const response = await api.post<ApiResponse<BroadcastCampaignStartResult>>(
      `/api/v1/broadcast/campaigns/${campaignId}/start`,
    )
    return response.data.data
  },

  async getCampaignStatus(campaignId: string): Promise<BroadcastCampaignStatus> {
    const response = await api.get<ApiResponse<BroadcastCampaignStatus>>(
      `/api/v1/broadcast/campaigns/${campaignId}/status`,
    )
    return response.data.data
  },

  async pauseCampaign(campaignId: string): Promise<BroadcastCampaign> {
    const response = await api.post<ApiResponse<BroadcastCampaign>>(
      `/api/v1/broadcast/campaigns/${campaignId}/pause`,
    )
    return response.data.data
  },

  async resumeCampaign(campaignId: string): Promise<BroadcastCampaignStatus> {
    const response = await api.post<ApiResponse<BroadcastCampaignStatus>>(
      `/api/v1/broadcast/campaigns/${campaignId}/resume`,
    )
    return response.data.data
  },

  async cancelCampaign(campaignId: string): Promise<BroadcastCampaign> {
    const response = await api.post<ApiResponse<BroadcastCampaign>>(
      `/api/v1/broadcast/campaigns/${campaignId}/cancel`,
    )
    return response.data.data
  },

  async listCampaignLogs(campaignId: string, limit = 50): Promise<BroadcastCampaignLog[]> {
    const response = await api.get<ApiResponse<BroadcastCampaignLog[]>>(
      `/api/v1/broadcast/campaigns/${campaignId}/logs`,
      { params: { limit } },
    )
    return response.data.data
  },
}

export default BroadcastsService
